import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LOGO_URL, waLink } from "../../lib/constants";
import { Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

export default function Footer() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const onBeforeInstall = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const handleInstallClick = async (e) => {
    e.preventDefault();
    if (isStandalone()) {
      toast.info("Swayambhu Gold is already installed on this device.");
      return;
    }
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (choice.outcome === "accepted") toast.success("Installing Swayambhu Gold…");
      return;
    }
    if (isIOS()) {
      toast.info('Tap the Share icon in Safari, then "Add to Home Screen".', { duration: 6000 });
      return;
    }
    toast.info('Open your browser menu and choose "Install app" or "Add to Home screen".', { duration: 6000 });
  };

  return (
    <footer className="bg-maroon-dark text-pearl/90 mt-24" data-testid="storefront-footer">
      <div className="px-6 lg:px-12 py-16 grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="logo" className="h-10 invert-0" />
            <div className="font-display text-2xl text-gold">Swayambhu Gold</div>
          </div>
          <p className="text-xs mt-4 text-pearl/70 leading-relaxed">Crafting heirloom jewellery for generations. BIS Hallmark. HUID Certified. Trusted across two showrooms — Belagavi & Khanapur.</p>
          <div className="flex gap-3 mt-5">
            <a href="https://instagram.com" target="_blank" rel="noreferrer" className="p-2 border border-pearl/30 hover:border-gold hover:text-gold transition-colors" aria-label="Instagram"><Instagram size={16}/></a>
            <a href="https://facebook.com" target="_blank" rel="noreferrer" className="p-2 border border-pearl/30 hover:border-gold hover:text-gold transition-colors" aria-label="Facebook"><Facebook size={16}/></a>
          </div>
        </div>
        <div>
          <div className="eyebrow text-gold mb-4">Shop</div>
          <ul className="text-sm space-y-2 text-pearl/70">
            <li><Link to="/catalog?category=Necklace" className="hover:text-gold">Necklaces</Link></li>
            <li><Link to="/catalog?category=Rings" className="hover:text-gold">Rings</Link></li>
            <li><Link to="/catalog?category=Earrings" className="hover:text-gold">Earrings</Link></li>
            <li><Link to="/catalog?category=Bangles" className="hover:text-gold">Bangles</Link></li>
            <li><Link to="/catalog?is_bestseller=true" className="hover:text-gold">Bestsellers</Link></li>
          </ul>
        </div>
        <div>
          <div className="eyebrow text-gold mb-4">Experience</div>
          <ul className="text-sm space-y-2 text-pearl/70">
            <li><Link to="/astrology" className="hover:text-gold">Consult Astrologer</Link></li>
            <li><Link to="/gold-programs" className="hover:text-gold">Gold Programs</Link></li>
            <li><Link to="/exchange" className="hover:text-gold">Gold Exchange</Link></li>
            <li><Link to="/blogs" className="hover:text-gold">Journal</Link></li>
            <li><Link to="/about" className="hover:text-gold">Our Story</Link></li>
            {/* <li><a href="#" onClick={handleInstallClick} className="hover:text-gold" data-testid="footer-install-app">Install App</a></li> */}
          </ul>
        </div>
        <div>
          <div className="eyebrow text-gold mb-4">Reach us</div>
          <ul className="text-sm space-y-3 text-pearl/70">
            <li><a href="https://maps.app.goo.gl/dCJrbLK4BVHuBVMr9" target="_blank" rel="noreferrer" className="flex items-start gap-2 hover:text-gold transition-colors" data-testid="footer-maps-link"><MapPin size={14} className="mt-0.5 shrink-0"/> Samadevi galli , Belagavi 590001</a></li>
            <li className="flex items-center gap-2"><Phone size={14}/> +91 9980187987</li>
            <li className="flex items-center gap-2"><Mail size={14}/> care@swayambhugold.com</li>
            <li><a href={waLink()} target="_blank" rel="noreferrer" className="text-gold hover:underline">Chat on WhatsApp →</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-pearl/15 px-6 lg:px-12 py-4 text-[11px] flex flex-col md:flex-row items-center justify-between gap-2">
        <span className="text-pearl/50">© {new Date().getFullYear()} Swayambhu Gold Private Limited · GSTIN 29ABCDE1234F1Z5</span>
        <div className="flex gap-4 text-pearl/50">
            <Link to="/staff/login" className="hover:text-gold" data-testid="footer-staff-link">Staff Login</Link>
            <Link to="/account" className="hover:text-gold">My Account</Link>
            <Link to="/privacy" className="hover:text-gold">Privacy / DPDP</Link>
            <Link to="/contact" className="hover:text-gold">Contact</Link>
        </div>
      </div>
      <div className="border-t border-pearl/15 px-6 lg:px-12 py-3 text-center text-[11px] text-pearl/50">
        Developed by <a href="https://x7it.com" target="_blank" rel="noreferrer" className="text-gold no-underline hover:underline" data-testid="footer-x7-credit">X7 IT TECHNOLOGIES</a>
      </div>
    </footer>
  );
}
